import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Button, Empty, Form, Input, List, Modal, Space, Tabs, Tag, Typography } from "antd";
import type { TabsProps } from "antd/lib";
import { EditOutlined, SearchOutlined } from "@ant-design/icons";

import type { Tenant } from "@chirpstack/chirpstack-api-grpc-web/api/tenant_pb";
import { Region, MacVersion } from "@chirpstack/chirpstack-api-grpc-web/common/common_pb";
import type {
  DeviceProfileListItem,
  DeviceProfileVendorListItem,
  DeviceProfileDeviceListItem,
  ListDeviceProfilesResponse,
  ListDeviceProfileVendorsResponse,
  ListDeviceProfileDevicesResponse,
  GetDeviceProfileResponse,
} from "@chirpstack/chirpstack-api-grpc-web/api/device_profile_pb";
import {
  ListDeviceProfilesRequest,
  ListDeviceProfileVendorsRequest,
  ListDeviceProfileDevicesRequest,
  GetDeviceProfileRequest,
} from "@chirpstack/chirpstack-api-grpc-web/api/device_profile_pb";

import DeviceProfileStore from "../stores/DeviceProfileStore";
import { usePlanGates } from "../contexts/PlanFeaturesContext";
import { getEnumName } from "../views/helpers";

// Datacake-inspired device-profile picker. Replaces the legacy 3-level
// Cascader with a button that opens a modal containing two tabs:
//
//   • "My Device Profiles"  — tenant-authored profiles (always shown)
//   • "Vendor Templates"    — EAMS-maintained vendor/device/profile tree
//                             (hidden when the tenant's plan has
//                             allowServerDeviceProfiles=false, unless
//                             the current selection is already bound to
//                             a vendor profile — so downgraded tenants
//                             can still see what their existing device
//                             points at).
//
// The vendor tab further filters per-device profiles through
// isRegionAllowed(): a Lite plan with allowedRegions=[EU868,US915,AU915]
// will only surface vendor profiles for those regions. This mirrors the
// enforcement applied in DeviceProfileForm's region dropdown.
//
// The component wraps an Ant Design Form.Item so it's a drop-in for the
// previous DeviceProfileSelect — the parent form still binds via `name`
// and reads a PLAIN STRING device-profile id from form state (versus
// the old Cascader which stored an array path).
//
// Known limitations (intentional, to match what exists today):
//   - We page-in up to 9999 entries per request. ioX-Bridge has no
//     cursor API; the whole catalog is small enough that this is fine.
//   - Search is a case-insensitive substring match performed
//     client-side. No fuzzy matching.

interface IProps {
  label: string;
  name: string;
  required?: boolean;
  tenant: Tenant;
  value?: string;
  disabled?: boolean;
  tooltip?: string;
}

// Shape displayed in the trigger button's summary row.
interface ProfileSummary {
  id: string;
  name: string;
  region: Region;
  macVersion: MacVersion;
  source: "tenant" | "vendor";
  vendorName?: string;
  deviceName?: string;
}

const LIMIT = 9999;

function macVersionLabel(v: MacVersion): string {
  // Match the enum labels used elsewhere in the UI. The `.replace("_", ".")`
  // trick upstream uses `LORAWAN_1_0_0` → "LORAWAN.1.0.0" which isn't
  // what we want; spell it out.
  switch (v) {
    case MacVersion.LORAWAN_1_0_0:
      return "1.0.0";
    case MacVersion.LORAWAN_1_0_1:
      return "1.0.1";
    case MacVersion.LORAWAN_1_0_2:
      return "1.0.2";
    case MacVersion.LORAWAN_1_0_3:
      return "1.0.3";
    case MacVersion.LORAWAN_1_0_4:
      return "1.0.4";
    case MacVersion.LORAWAN_1_1_0:
      return "1.1.0";
    default:
      return "";
  }
}

function regionLabel(r: Region): string {
  return getEnumName(Region, r).replace("_", "-");
}

function matches(needle: string, ...haystacks: string[]): boolean {
  if (needle === "") return true;
  const n = needle.toLowerCase();
  return haystacks.some(h => h.toLowerCase().includes(n));
}

function DeviceProfilePicker(props: IProps) {
  const form = Form.useFormInstance();
  const { canUseServerDeviceProfiles, isRegionAllowed } = usePlanGates();

  // Current-selection summary is resolved lazily via GetDeviceProfile so the
  // trigger button can show "Name · Region · MAC x.y.z" without requiring
  // the caller to pre-hydrate the value.
  const [summary, setSummary] = useState<ProfileSummary | null>(null);
  const [open, setOpen] = useState(false);

  // Re-entrancy guard so simultaneous props.value changes (e.g. QR scan
  // updating deviceProfileId while the user has the modal open) don't
  // interleave fetches.
  const resolvedValueRef = useRef<string | undefined>(undefined);

  // ─── Resolve current selection into a summary for the closed button
  useEffect(() => {
    if (!props.value) {
      setSummary(null);
      resolvedValueRef.current = undefined;
      return;
    }
    if (resolvedValueRef.current === props.value) return;
    resolvedValueRef.current = props.value;

    const req = new GetDeviceProfileRequest();
    req.setId(props.value);
    DeviceProfileStore.get(req, (resp: GetDeviceProfileResponse) => {
      const dp = resp.getDeviceProfile();
      if (!dp) return;
      setSummary({
        id: dp.getId(),
        name: dp.getName(),
        region: dp.getRegion(),
        macVersion: dp.getMacVersion(),
        source: dp.getDeviceId() !== "" ? "vendor" : "tenant",
      });
      // Keep the form's value in sync — parent component binds by `name`.
      form.setFieldValue(props.name, dp.getId());
    });
  }, [props.value, props.name, form]);

  // Whether the Vendor tab should be reachable. Hidden when the plan
  // disallows it, EXCEPT when the existing value is already a vendor
  // profile — we surface the tab so the user can see the current binding
  // even on a downgraded plan. Picking a NEW vendor profile is still
  // implicitly prevented because the tab's profiles list runs through
  // isRegionAllowed + the plan gate at request time.
  const showVendorTab = canUseServerDeviceProfiles() || summary?.source === "vendor";

  // ─── "My Device Profiles" tab state
  const [myProfiles, setMyProfiles] = useState<DeviceProfileListItem[]>([]);
  const [mySearch, setMySearch] = useState("");
  const [myLoading, setMyLoading] = useState(false);

  const loadMyProfiles = useCallback(() => {
    setMyLoading(true);
    const req = new ListDeviceProfilesRequest();
    req.setTenantId(props.tenant.getId());
    req.setTenantOnly(true);
    req.setLimit(LIMIT);
    DeviceProfileStore.list(req, (resp: ListDeviceProfilesResponse) => {
      setMyProfiles(resp.getResultList());
      setMyLoading(false);
    });
  }, [props.tenant]);

  // ─── "Vendor Templates" tab state
  type VendorStep = "vendor" | "device" | "profile";
  const [vendorStep, setVendorStep] = useState<VendorStep>("vendor");
  const [vendors, setVendors] = useState<DeviceProfileVendorListItem[]>([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [vendorSearch, setVendorSearch] = useState("");

  const [pickedVendor, setPickedVendor] = useState<DeviceProfileVendorListItem | null>(null);
  const [devices, setDevices] = useState<DeviceProfileDeviceListItem[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [deviceSearch, setDeviceSearch] = useState("");

  const [pickedDevice, setPickedDevice] = useState<DeviceProfileDeviceListItem | null>(null);
  const [profiles, setProfiles] = useState<DeviceProfileListItem[]>([]);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [profileSearch, setProfileSearch] = useState("");

  const loadVendors = useCallback(() => {
    setVendorsLoading(true);
    const req = new ListDeviceProfileVendorsRequest();
    req.setLimit(LIMIT);
    DeviceProfileStore.listVendors(req, (resp: ListDeviceProfileVendorsResponse) => {
      setVendors(resp.getResultList());
      setVendorsLoading(false);
    });
  }, []);

  const loadDevices = useCallback((vendorId: string) => {
    setDevicesLoading(true);
    const req = new ListDeviceProfileDevicesRequest();
    req.setVendorId(vendorId);
    req.setLimit(LIMIT);
    DeviceProfileStore.listDevices(req, (resp: ListDeviceProfileDevicesResponse) => {
      setDevices(resp.getResultList());
      setDevicesLoading(false);
    });
  }, []);

  const loadProfiles = useCallback((deviceId: string) => {
    setProfilesLoading(true);
    const req = new ListDeviceProfilesRequest();
    req.setDeviceId(deviceId);
    req.setGlobalOnly(true);
    req.setLimit(LIMIT);
    DeviceProfileStore.list(req, (resp: ListDeviceProfilesResponse) => {
      setProfiles(resp.getResultList());
      setProfilesLoading(false);
    });
  }, []);

  // Load lazily when the modal opens so we don't hit the API on every
  // form mount.
  useEffect(() => {
    if (!open) return;
    if (myProfiles.length === 0 && !myLoading) loadMyProfiles();
    if (showVendorTab && vendors.length === 0 && !vendorsLoading) loadVendors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Reset vendor-tab navigation whenever the modal is closed so the next
  // open starts at the top of the tree.
  const handleClose = () => {
    setOpen(false);
    setVendorStep("vendor");
    setPickedVendor(null);
    setPickedDevice(null);
    setVendorSearch("");
    setDeviceSearch("");
    setProfileSearch("");
  };

  const selectProfile = (id: string, summaryPatch: ProfileSummary) => {
    form.setFieldValue(props.name, id);
    form.validateFields([props.name]);
    setSummary(summaryPatch);
    handleClose();
  };

  // ─── Filtered views
  const filteredMyProfiles = useMemo(
    () =>
      myProfiles.filter(p =>
        matches(mySearch, p.getName(), regionLabel(p.getRegion()), macVersionLabel(p.getMacVersion())),
      ),
    [myProfiles, mySearch],
  );

  const filteredVendors = useMemo(
    () => vendors.filter(v => matches(vendorSearch, v.getName())),
    [vendors, vendorSearch],
  );

  const filteredDevices = useMemo(
    () => devices.filter(d => matches(deviceSearch, d.getName())),
    [devices, deviceSearch],
  );

  // Profiles are where region gating bites — a vendor device may publish
  // profiles for ten regions; we only show the ones the plan permits.
  const filteredProfiles = useMemo(
    () =>
      profiles.filter(
        p =>
          isRegionAllowed(p.getRegion()) &&
          matches(profileSearch, p.getName(), regionLabel(p.getRegion()), p.getFirmwareVersion()),
      ),
    [profiles, profileSearch, isRegionAllowed],
  );

  // ─── Render helpers
  const myTab = (
    <div>
      <Input
        prefix={<SearchOutlined />}
        placeholder="Search my device profiles"
        value={mySearch}
        onChange={e => setMySearch(e.target.value)}
        allowClear
        style={{ marginBottom: 12 }}
      />
      {!myLoading && filteredMyProfiles.length === 0 ? (
        <Empty
          description={
            myProfiles.length === 0
              ? "No device profiles yet. Create one from Device profiles → Add device profile."
              : "No matches"
          }
        />
      ) : (
        <List
          loading={myLoading}
          dataSource={filteredMyProfiles}
          size="small"
          style={{ maxHeight: 360, overflowY: "auto" }}
          renderItem={p => (
            <List.Item
              style={{ cursor: "pointer" }}
              onClick={() =>
                selectProfile(p.getId(), {
                  id: p.getId(),
                  name: p.getName(),
                  region: p.getRegion(),
                  macVersion: p.getMacVersion(),
                  source: "tenant",
                })
              }
            >
              <List.Item.Meta
                title={p.getName()}
                description={
                  <Space size={4}>
                    <Tag>{regionLabel(p.getRegion())}</Tag>
                    <Tag>MAC {macVersionLabel(p.getMacVersion())}</Tag>
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      )}
    </div>
  );

  const breadcrumb = (
    <Space size={4} style={{ marginBottom: 12 }}>
      <Button
        size="small"
        type={vendorStep === "vendor" ? "primary" : "default"}
        onClick={() => {
          setVendorStep("vendor");
          setPickedVendor(null);
          setPickedDevice(null);
        }}
      >
        Vendors
      </Button>
      {pickedVendor && (
        <Button
          size="small"
          type={vendorStep === "device" ? "primary" : "default"}
          onClick={() => {
            setVendorStep("device");
            setPickedDevice(null);
          }}
        >
          {pickedVendor.getName()}
        </Button>
      )}
      {pickedDevice && (
        <Button size="small" type={vendorStep === "profile" ? "primary" : "default"} disabled>
          {pickedDevice.getName()}
        </Button>
      )}
    </Space>
  );

  const vendorTab = (
    <div>
      {breadcrumb}
      {vendorStep === "vendor" && (
        <>
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search vendors"
            value={vendorSearch}
            onChange={e => setVendorSearch(e.target.value)}
            allowClear
            style={{ marginBottom: 12 }}
          />
          <List
            loading={vendorsLoading}
            dataSource={filteredVendors}
            size="small"
            style={{ maxHeight: 360, overflowY: "auto" }}
            locale={{ emptyText: <Empty description="No vendors" /> }}
            renderItem={v => (
              <List.Item
                style={{ cursor: "pointer" }}
                onClick={() => {
                  setPickedVendor(v);
                  setVendorStep("device");
                  setDeviceSearch("");
                  loadDevices(v.getId());
                }}
              >
                <List.Item.Meta title={v.getName()} />
              </List.Item>
            )}
          />
        </>
      )}
      {vendorStep === "device" && pickedVendor && (
        <>
          <Input
            prefix={<SearchOutlined />}
            placeholder={`Search ${pickedVendor.getName()} devices`}
            value={deviceSearch}
            onChange={e => setDeviceSearch(e.target.value)}
            allowClear
            style={{ marginBottom: 12 }}
          />
          <List
            loading={devicesLoading}
            dataSource={filteredDevices}
            size="small"
            style={{ maxHeight: 360, overflowY: "auto" }}
            locale={{ emptyText: <Empty description="No devices" /> }}
            renderItem={d => (
              <List.Item
                style={{ cursor: "pointer" }}
                onClick={() => {
                  setPickedDevice(d);
                  setVendorStep("profile");
                  setProfileSearch("");
                  loadProfiles(d.getId());
                }}
              >
                <List.Item.Meta title={d.getName()} />
              </List.Item>
            )}
          />
        </>
      )}
      {vendorStep === "profile" && pickedVendor && pickedDevice && (
        <>
          <Input
            prefix={<SearchOutlined />}
            placeholder={`Search ${pickedDevice.getName()} profiles`}
            value={profileSearch}
            onChange={e => setProfileSearch(e.target.value)}
            allowClear
            style={{ marginBottom: 12 }}
          />
          {!profilesLoading && filteredProfiles.length === 0 ? (
            <Empty
              description={
                profiles.length > 0
                  ? "No profiles for this device match your plan's allowed regions."
                  : "No profiles available for this device."
              }
            />
          ) : (
            <List
              loading={profilesLoading}
              dataSource={filteredProfiles}
              size="small"
              style={{ maxHeight: 360, overflowY: "auto" }}
              renderItem={p => (
                <List.Item
                  style={{ cursor: "pointer" }}
                  onClick={() =>
                    selectProfile(p.getId(), {
                      id: p.getId(),
                      name: p.getName(),
                      region: p.getRegion(),
                      macVersion: p.getMacVersion(),
                      source: "vendor",
                      vendorName: pickedVendor.getName(),
                      deviceName: pickedDevice.getName(),
                    })
                  }
                >
                  <List.Item.Meta
                    title={p.getName()}
                    description={
                      <Space size={4}>
                        <Tag>{regionLabel(p.getRegion())}</Tag>
                        <Tag>MAC {macVersionLabel(p.getMacVersion())}</Tag>
                        {p.getFirmwareVersion() && <Tag>fw {p.getFirmwareVersion()}</Tag>}
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          )}
        </>
      )}
    </div>
  );

  const tabItems: TabsProps["items"] = [
    { key: "my", label: "My Device Profiles", children: myTab },
    ...(showVendorTab ? [{ key: "vendor", label: "Vendor Templates", children: vendorTab }] : []),
  ];

  const triggerLabel = summary ? (
    <Space size={6} wrap>
      <Typography.Text strong>{summary.name}</Typography.Text>
      <Tag>{regionLabel(summary.region)}</Tag>
      <Tag>MAC {macVersionLabel(summary.macVersion)}</Tag>
      {summary.source === "vendor" && summary.vendorName && <Tag color="blue">{summary.vendorName}</Tag>}
    </Space>
  ) : (
    <Typography.Text type="secondary">Select device profile…</Typography.Text>
  );

  return (
    <Form.Item
      label={props.label}
      name={props.name}
      tooltip={props.tooltip}
      rules={[{ required: props.required, message: `Please select a ${props.label}` }]}
    >
      {/* A hidden input carries the form value so Ant Design's Form
          machinery (validation, initialValues, setFieldValue) keeps
          working unchanged. The visible trigger is a Button that opens
          the modal. */}
      <Input type="hidden" />
      <Button
        block
        onClick={() => !props.disabled && setOpen(true)}
        disabled={props.disabled}
        style={{ height: "auto", minHeight: 32, textAlign: "left", whiteSpace: "normal" }}
        icon={<EditOutlined />}
      >
        {triggerLabel}
      </Button>
      <Modal
        title="Select device profile"
        open={open}
        onCancel={handleClose}
        footer={null}
        width={720}
        destroyOnClose
      >
        <Tabs defaultActiveKey="my" items={tabItems} />
      </Modal>
    </Form.Item>
  );
}

export default DeviceProfilePicker;
